// Extracted vs on file, per document.
//
// The surface the flagged-mismatch review turns on. Two columns and an edit
// affordance on the rows that have a write path — plus "use extracted", because
// the common case is that the scan is right and the record is empty or stale.
import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  TintedStatusPill,
  CircularProgress,
} from '../../ui/core';
import { hairline, softFill } from '../../ui/styles';
import { WarningIcon } from '../../ui/icons';
import { SectionLabel, Hair } from './components';
import { comparedCount, contestedCount, fillableRows, mismatchCount } from './fields';

/** How a run reads when it is not a finished success. */
const RUN_NOTE = {
  RUNNING: 'Verification is still running — these fields are not final.',
  FAILED: 'The last verification run failed. Nothing was extracted from it.',
};

// No agreement pill. "models disagree" described the run in the models' own terms,
// which is not a fact a reviewer acts on — what they act on is how many fields need a
// choice, and the header's "N to pick" says that from the rows themselves. The two
// also contradicted each other on a run that split on one field and agreed on the
// rest.

/** `anthropic/claude-sonnet-4.5` → `claude-sonnet-4.5`. The vendor is noise here. */
const shortModel = (name) => String(name).split('/').pop();

const Value = ({ children, dim, nowrap }) => (
  <Typography
    sx={{
      fontSize: '12.5px',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      color: dim ? 'text.disabled' : 'text.primary',
      // A candidate value is compared character by character against another one, so
      // it must not break mid-value — the model names beside it wrap instead.
      ...(nowrap ? { whiteSpace: 'nowrap' } : { wordBreak: 'break-word' }),
    }}
  >
    {children || '—'}
  </Typography>
);

/**
 * The extracted answers for one field, as choices, in the Extracted column itself.
 *
 * This replaced a separate "what each model read" section below the table, which
 * listed one card per model — so resolving a disagreement meant reading three cards,
 * transposing them into per-field columns by eye, and typing the winner in by hand.
 *
 * Every value is selectable, including a lone one: "one choice" and "several choices"
 * are the same interaction, and a reviewer copying the single extracted value onto an
 * empty record is the most common act on this panel by far. Which model read what is
 * deliberately NOT shown — the reviewer is choosing between VALUES, and the model
 * names were noise wrapped around them. They still order the list, most-agreed first.
 *
 * Picking fills the draft rather than saving, so a choice is still reviewed before it
 * lands, and it lands through the one save path instead of a second write.
 */
const Candidates = ({ candidates, selected, onPick, editable }) => (
  <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 0.5 }}>
    {candidates.map((candidate) => {
      const isSelected = selected !== undefined && selected === candidate.value;
      return (
        <Box
          key={candidate.value}
          onClick={editable ? () => onPick(candidate.value) : undefined}
          sx={{
            px: 0.75,
            py: 0.4,
            borderRadius: '6px',
            border: (t) => `1px solid ${isSelected ? t.palette.accent.main : hairline(t)}`,
            backgroundColor: (t) => (isSelected ? softFill(t) : 'transparent'),
            cursor: editable ? 'pointer' : 'default',
            ...(editable && { '&:hover': { borderColor: (t) => t.palette.accent.main } }),
          }}
        >
          <Value nowrap>{candidate.value}</Value>
        </Box>
      );
    })}
  </Stack>
);

/**
 * One paired fact.
 *
 * Three states worth telling apart, and the colour is the only thing that carries
 * it: they disagree (warning), nothing is on file (neutral — a gap, not a fault),
 * or they match (quiet).
 */
/**
 * What `dateText` produces, and what `<input type="date">` requires as a value.
 * A draft that does not look like this is shown as text — see `dateInput` below.
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Bounds for a date input. Not decoration: Mongo holds registration expiries with
 * five-digit years (20222 for 2022), and one of those crashed a backfill before it
 * was guarded. A native input that refuses to produce one is cheaper than a
 * validator that catches it later.
 */
const DATE_MIN = '1900-01-01';
const DATE_MAX = '2100-12-31';

const Row = ({ row, editing, draft, onDraft, onEdit }) => {
  const disagrees = row.agrees === false;
  const candidates = row.candidates || [];
  // A date row gets a date input, unless what is stored is not an ISO date — in
  // which case text, so the reviewer can see the odd value rather than a blank box.
  const dateInput = row.isDate && (!draft || ISO_DATE.test(draft));
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        borderTop: (t) => `1px solid ${hairline(t)}`,
        backgroundColor: (t) => (disagrees || row.contested ? softFill(t) : 'transparent'),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        {(disagrees || row.contested) && (
          <WarningIcon sx={{ fontSize: 14, display: 'block', color: 'warning.main', flexShrink: 0 }} />
        )}
        <Typography sx={{ fontSize: '11.5px', fontWeight: 600, color: 'text.secondary', flex: 1 }}>
          {row.label}
        </Typography>
        {row.editable && !editing && (
          <Typography
            onClick={onEdit}
            sx={{ fontSize: '11.5px', color: 'accent.main', fontWeight: 600, cursor: 'pointer' }}
          >
            Edit
          </Typography>
        )}
      </Stack>
      <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '10.5px', color: 'text.disabled', mb: 0.25 }}>
            {row.contested ? `Extracted — pick one (${candidates.length})` : 'Extracted'}
          </Typography>
          {candidates.length ? (
            <Candidates
              candidates={candidates}
              selected={editing ? draft : undefined}
              onPick={onDraft}
              editable={row.editable}
            />
          ) : (
            <Value dim />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '10.5px', color: 'text.disabled', mb: 0.25 }}>On file</Typography>
          {editing ? (
            <TextField
              autoFocus
              size="small"
              fullWidth
              // A real date input on date rows: the format is enforced by the
              // browser, the value it emits is YYYY-MM-DD (which is what the API's
              // IsISO8601 wants), and a reviewer gets a picker instead of guessing
              // whether we accept 05/22/2034 or 2034-05-22. Free text accepted both
              // and stored whichever was typed.
              //
              // FALLS BACK TO TEXT for a value that is not already an ISO date.
              // `dateText` passes unparseable input through verbatim rather than
              // rendering "Invalid date", on the principle that a reviewer should
              // see what is actually stored — and a date input given that would show
              // blank, hiding it at the moment it most needs looking at.
              type={dateInput ? 'date' : 'text'}
              inputProps={dateInput ? { min: DATE_MIN, max: DATE_MAX } : undefined}
              value={draft ?? ''}
              onChange={(e) => onDraft(e.target.value)}
              sx={{ '& input': { fontSize: '12.5px', py: 0.5 } }}
            />
          ) : (
            // `source: null` means nothing stores this fact, which is different
            // from stored-but-empty. Saying so stops it reading as missing data.
            <Value dim={!row.onFile}>{row.onFile ?? (row.source ? null : 'not stored')}</Value>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

const FieldsPanel = ({ rows, run, loading, error, onSave, onReplay, busy }) => {
  // Drafts by field key.
  //
  // Cleared when a DIFFERENT subject arrives underneath — another document opened, or
  // rows added/removed — where a surviving draft would sit over an unrelated value.
  // NOT sufficient for the save path: correcting an on-file value re-reads the record
  // without changing the run id or the row count, so the effect never fired and the
  // open fields stayed open over the saved values, inviting a redundant re-submit.
  // `save` handles that, on success only.
  const [drafts, setDrafts] = useState({});
  useEffect(() => setDrafts({}), [run?.id, rows.length]);

  const editing = Object.keys(drafts);
  const mismatches = mismatchCount(rows);
  const contested = contestedCount(rows);
  const compared = comparedCount(rows);
  const fillable = fillableRows(rows);

  const useExtracted = () => {
    setDrafts((current) => ({
      ...current,
      ...Object.fromEntries(fillable.map((row) => [row.key, row.extracted])),
    }));
  };

  const save = async () => {
    // Only rows that are actually editable, keyed by where they live, so the
    // caller can route each to the right write path.
    const byRow = rows.filter((row) => row.editable && drafts[row.key] !== undefined);
    // Cleared on success only. On a failure the drafts are the reviewer's unsaved
    // typing and the error toast is asking them to retry — wiping the fields would
    // make them retype everything.
    if (await onSave(byRow.map((row) => ({ ...row, value: drafts[row.key] })))) {
      setDrafts({});
    }
  };

  if (loading) {
    return (
      <Box>
        <SectionLabel sx={{ display: 'block', mb: 1 }}>Extracted vs on file</SectionLabel>
        <Stack alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={16} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      {/* This line is the review verdict — what a reviewer has to DO. How the run
          itself went (agreement, model count) is the line below, so the two can no
          longer contradict each other the way "all match" beside "models disagree"
          did. */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        {/* Honest about what is actually on screen: with no run there is no left
            column to compare against, and calling it a comparison would imply the
            extraction found nothing rather than never having looked. */}
        <SectionLabel>{run ? 'Extracted vs on file' : 'On file'}</SectionLabel>
        {/* Two different jobs, so two counts rather than one sum: a mismatch is
            decided against the document, a contested field is decided between the
            models first. */}
        {contested > 0 && <TintedStatusPill palette="warning" label={`${contested} to pick`} />}
        {mismatches > 0 && <TintedStatusPill palette="warning" label={`${mismatches} to resolve`} />}
        {/* Only when a comparison actually happened. Nothing to resolve, nothing to
            pick, and at least one field where both sides had a value. */}
        {mismatches === 0 && contested === 0 && compared > 0 && run?.status === 'SUCCEEDED' && (
          <TintedStatusPill palette="success" label="all match" />
        )}
      </Stack>

      {error && (
        <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 1 }}>{error}</Typography>
      )}

      {/* Run metadata is the only part gated on there being a run. The FIELDS are
          not: a document with no extraction still has values on file, and those
          are the whole point for a driver who applied on the old app — no run
          ever touched their documents, and this is where their details get
          entered. Hiding the table because the left column is empty would hide
          the one thing that case needs. */}
      {run ? (
        <>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
            {run.modelsRun?.length > 0 && (
              <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>
                {run.models?.filter((m) => m.ok).length ?? 0}/{run.modelsRun.length} models
              </Typography>
            )}
            {typeof run.confidence === 'number' && (
              <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                self-reported {Math.round(run.confidence * 100)}%
              </Typography>
            )}
            {/* Worth surfacing loudly: the model saying it is looking at a
                different document than we asked for explains every mismatch
                below it. */}
            {run.detectedDocumentType && (
              <TintedStatusPill
                palette={run.detectedDocumentType === 'other' ? 'warning' : null}
                label={`read as ${run.detectedDocumentType}`}
              />
            )}
          </Stack>

          {RUN_NOTE[run.status] && (
            <Typography sx={{ fontSize: '12px', color: 'warning.main', mb: 1 }}>{RUN_NOTE[run.status]}</Typography>
          )}

          {/* A failed model is the one thing the rows cannot carry: it contributed no
              value anywhere, so it appears in no field's candidates. "The model
              errored" and "the model read nothing" are different, and only one is a
              data problem — so the reason is named. */}
          {run.models?.some((model) => !model.ok) && (
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mb: 1 }}>
              {run.models
                .filter((model) => !model.ok)
                .map((model) => `${shortModel(model.model)} failed${model.error ? `: ${model.error}` : ''}`)
                .join(' · ')}
            </Typography>
          )}

          {run.validationIssues?.length > 0 && (
            <Box sx={{ mb: 1 }}>
              {run.validationIssues.map((issue) => (
                <Typography key={issue} sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  • {issue}
                </Typography>
              ))}
            </Box>
          )}
        </>
      ) : (
        !error && (
          // Normal for a legacy row, not a fault: verification runs on upload, and
          // runs written before the document link existed cannot be attributed to
          // a document at all.
          <Typography sx={{ fontSize: '12px', color: 'text.disabled', mb: 1 }}>
            Nothing extracted from this document — enter the values from the scan yourself.
          </Typography>
        )
      )}

      {rows.length > 0 && (
        <Box sx={{ border: (t) => `1px solid ${hairline(t)}`, borderRadius: '8px', overflow: 'hidden' }}>
          {rows.map((row) => (
            <Row
              key={row.key}
              row={row}
              editing={drafts[row.key] !== undefined}
              draft={drafts[row.key]}
              onDraft={(value) => setDrafts((current) => ({ ...current, [row.key]: value }))}
              onEdit={() => setDrafts((current) => ({ ...current, [row.key]: row.onFile ?? '' }))}
            />
          ))}
        </Box>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
        {/* The mismatch case in one click: the scan has the answer and the record
            does not agree. Fills drafts rather than saving, so it is still reviewed
            before it lands. Absent with no run, since there is nothing to copy. */}
        {fillable.length > 0 && (
          <Button size="small" variant="outlined" onClick={useExtracted} sx={{ fontSize: '12px', textTransform: 'none' }}>
            Use extracted ({fillable.length})
          </Button>
        )}
        {editing.length > 0 && (
          <>
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              onClick={save}
              sx={{ fontSize: '12px', textTransform: 'none' }}
            >
              {busy ? 'Saving…' : `Save ${editing.length}`}
            </Button>
            <Button size="small" onClick={() => setDrafts({})} sx={{ fontSize: '12px', textTransform: 'none' }}>
              Cancel
            </Button>
          </>
        )}
      </Stack>

      {/* Re-run the pipeline over this document.

          Here rather than beside the decision, because this is the panel that shows
          what the pipeline produced — and the two states that need it are both
          visible from here: nothing extracted at all (the legacy and backfilled
          rows), and an extraction that is fine but was decided under a rule since
          fixed. Text buttons: it is a recovery affordance, not part of the review
          flow, and it must not compete with Approve.

          Re-decide is offered only when there IS a stored extraction. Without one
          the server refuses it, and a button whose only outcome is an error is
          worse than no button. */}
      {onReplay && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="text"
            disabled={busy}
            onClick={() => onReplay('extraction')}
            sx={{ fontSize: '11.5px', textTransform: 'none' }}
          >
            {run ? 'Re-read this document' : 'Read this document'}
          </Button>
          {run && (
            <Button
              size="small"
              variant="text"
              disabled={busy}
              onClick={() => onReplay('decision')}
              sx={{ fontSize: '11.5px', textTransform: 'none' }}
            >
              Re-decide from this extraction
            </Button>
          )}
        </Stack>
      )}
      {onReplay && (
        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: 0.25 }}>
          {/* Says what each costs, because one of them is billable and a reviewer
              clicking through a queue has no other way to know. */}
          Reading runs the models again; re-deciding replays what they already read. Neither overrides a decision
          already made by a person.
        </Typography>
      )}

      {rows.some((row) => !row.source) && (
        <>
          <Hair sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
            Rows marked “not stored” are read off the document only — there is nowhere to keep them, so they cannot be
            corrected.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default FieldsPanel;
